import type { HTMLAttributes, ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * Minimal Badge primitive used by TopBar / Results headers / per-query rows.
 * Variant styling uses CSS vars (oklch tokens) so light/dark is automatic.
 */

export type BadgeVariant = "outline" | "secondary" | "warn" | "gray";

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  children: ReactNode;
}

export function Badge({ variant = "outline", className, style, children, ...rest }: BadgeProps) {
  const base: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: 6,
    fontSize: 11,
    lineHeight: 1.4,
    fontFamily: "var(--font-sans)",
    fontWeight: 500,
    whiteSpace: "nowrap",
  };
  const v: Record<BadgeVariant, React.CSSProperties> = {
    outline: {
      border: "1px solid var(--border)",
      background: "var(--background)",
      color: "var(--muted-foreground)",
    },
    secondary: {
      border: "1px solid var(--border)",
      background: "var(--secondary)",
      color: "var(--muted-foreground)",
    },
    gray: {
      border: "1px solid var(--border)",
      background: "var(--secondary)",
      color: "var(--muted-foreground)",
    },
    warn: {
      border: "1px solid color-mix(in oklch, var(--warn) 60%, transparent)",
      background: "color-mix(in oklch, var(--warn) 35%, white)",
      color: "var(--warn-foreground)",
    },
  };
  return (
    <span {...rest} className={cn(className)} style={{ ...base, ...v[variant], ...style }}>
      {children}
    </span>
  );
}
